import { useTranslation } from 'react-i18next';
import { Importance } from '@/types/evaluation.types';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, AlertTriangle, Info, Minus, Star } from 'lucide-react';

interface ImportanceSelectorProps {
  value: Importance;
  onChange: (value: Importance) => void;
  id?: string;
  disabled?: boolean;
}

const importanceIcons = {
  [Importance.Critical]: <Star className="h-4 w-4 text-red-600" aria-hidden="true" />,
  [Importance.High]: <AlertTriangle className="h-4 w-4 text-orange-600" aria-hidden="true" />,
  [Importance.Medium]: <AlertCircle className="h-4 w-4 text-yellow-600" aria-hidden="true" />,
  [Importance.Low]: <Info className="h-4 w-4 text-blue-600" aria-hidden="true" />,
  [Importance.None]: <Minus className="h-4 w-4 text-gray-400" aria-hidden="true" />,
};

export function ImportanceSelector({
  value,
  onChange,
  id = 'importance',
  disabled = false,
}: ImportanceSelectorProps) {
  const { t } = useTranslation('criteria');

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{t('form.importance.label')}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} aria-label={t('form.importance.label')}>
          <SelectValue>
            <div className="flex items-center gap-2">
              {importanceIcons[value]}
              <span>{t(`form.importance.levels.${value.toLowerCase()}`)}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={Importance.Critical}>
            <div className="flex items-center gap-2">
              {importanceIcons[Importance.Critical]}
              <div className="flex flex-col items-start">
                <span className="font-medium">{t('form.importance.levels.critical')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('form.importance.descriptions.critical')}
                </span>
              </div>
            </div>
          </SelectItem>
          <SelectItem value={Importance.High}>
            <div className="flex items-center gap-2">
              {importanceIcons[Importance.High]}
              <div className="flex flex-col items-start">
                <span className="font-medium">{t('form.importance.levels.high')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('form.importance.descriptions.high')}
                </span>
              </div>
            </div>
          </SelectItem>
          <SelectItem value={Importance.Medium}>
            <div className="flex items-center gap-2">
              {importanceIcons[Importance.Medium]}
              <div className="flex flex-col items-start">
                <span className="font-medium">{t('form.importance.levels.medium')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('form.importance.descriptions.medium')}
                </span>
              </div>
            </div>
          </SelectItem>
          <SelectItem value={Importance.Low}>
            <div className="flex items-center gap-2">
              {importanceIcons[Importance.Low]}
              <div className="flex flex-col items-start">
                <span className="font-medium">{t('form.importance.levels.low')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('form.importance.descriptions.low')}
                </span>
              </div>
            </div>
          </SelectItem>
          <SelectItem value={Importance.None}>
            <div className="flex items-center gap-2">
              {importanceIcons[Importance.None]}
              <div className="flex flex-col items-start">
                <span className="font-medium">{t('form.importance.levels.none')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('form.importance.descriptions.none')}
                </span>
              </div>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {t(`form.importance.descriptions.${value.toLowerCase()}`)}
      </p>
    </div>
  );
}
